import { useEffect } from 'react'
import { Content, PersonalizedVariant, Variant } from '@phrasee/phrasee-typings'
import { useQueryClient } from '@tanstack/react-query'
import * as types from 'redux/actionTypes'
import { SubjectLine } from 'workflow/interface'

import { errorToast } from 'common/components/toastNotification'
import { useAppDispatch } from 'common/hooks/redux'
import useWebsockets, { WsResponseBase } from 'common/hooks/useWebsockets'
import { contentKeys } from 'features/unifiedFlow/api/queryKeys'
import {
  useContent,
  useSelectedComponentElement,
} from 'features/unifiedFlow/contentPage/hooks'
import useWebsocketMessage from 'features/unifiedFlow/contentPage/hooks/websocketMessage'

import useVariantsToApproveQuery from './useVariantsToApproveQuery'

interface WsResponse extends WsResponseBase {
  content?: Content // Optional for backwards compatibility
  elementId?: number // Element ID for the modified variant
  oldVariantId?: string // Old variant ID for replacements
  sl?: SubjectLine
  baselineVariant?: Variant
  personalizedVariant?: PersonalizedVariant
}

type Props = {
  onTweakSuccess?: (newVariantId: string) => void
}

const useVariantTweakWebsocketSubscription = ({ onTweakSuccess }: Props) => {
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()

  const {
    lastJsonMessage,
  }: {
    lastJsonMessage: WsResponse
  } = useWebsockets()
  const { content } = useContent()
  const { sendWebsocketMessage } = useWebsocketMessage()
  const { data: selectedElement } = useSelectedComponentElement()
  const { invalidateAll: invalidateAllVariantsToApproveCountQuery } =
    useVariantsToApproveQuery()

  const handlePersonalizedCampaign = (
    wsTopicElementId: string,
    wsTopicVariantId: string,
    action: 'tweak' | 'reject'
  ) => {
    invalidateAllVariantsToApproveCountQuery()

    if (!content) {
      return
    }

    // New approach: Update only the specific variant
    if (lastJsonMessage.personalizedVariant && !lastJsonMessage.content) {
      queryClient.setQueryData(
        contentKeys.content(content._id),
        (oldContent: Content | undefined) => {
          if (!oldContent) {
            return oldContent
          }

          return {
            ...oldContent,
            elements: oldContent.elements.map((element) => {
              if (
                element.element_id === Number(wsTopicElementId) &&
                'personalized_variants' in element
              ) {
                const newVariantId =
                  lastJsonMessage.personalizedVariant?.variant_id
                const oldVariantId = wsTopicVariantId

                // Update personalized_ids_to_approve to replace old variant ID with new one
                const updatedIdsToApprove = element.personalized_ids_to_approve
                  ?.map((id) => (id === oldVariantId ? newVariantId : id))
                  .filter((id): id is string => id !== undefined)

                // Remove the old variant and add the new one at the beginning (for user-modified variants to appear first)
                // For reject actions, we need to remove why_tweak so it doesn't appear in "User modified" section
                const newVariant = {
                  ...lastJsonMessage.personalizedVariant,
                  isLoading: false,
                  // Remove why_tweak for reject actions so variant appears in "Variants to approve" section
                  ...(action === 'reject' && { why_tweak: undefined }),
                } as PersonalizedVariant

                // Find the index of the old variant to maintain position for reject actions
                const oldVariantIndex =
                  element.personalized_variants?.findIndex(
                    (variant) => variant.variant_id === wsTopicVariantId
                  )

                const variantsWithoutOld =
                  element.personalized_variants?.filter(
                    (variant) => variant.variant_id !== wsTopicVariantId
                  )

                // Determine where to place the new variant:
                // - For tweak actions with why_tweak: put at the beginning (top of user-modified section after table sorts)
                // - For reject actions on user-modified variants: insert right before first user-modified variant (bottom of non-modified section)
                // - For reject actions on non-modified variants: maintain position
                let updatedVariants: PersonalizedVariant[]

                // Check if the old variant was user-modified
                const oldVariant = element.personalized_variants?.find(
                  (variant) => variant.variant_id === wsTopicVariantId
                )
                const wasUserModified = !!oldVariant?.why_tweak

                if (action === 'tweak' && newVariant.why_tweak) {
                  // Edited variant goes to the beginning
                  updatedVariants = [newVariant, ...(variantsWithoutOld || [])]
                } else if (action === 'reject' && wasUserModified) {
                  // Rejected user-modified variant should appear at bottom of non-modified section (top section)
                  // Find the last index of a non-modified variant (without why_tweak)
                  let lastNonModifiedIndex = -1
                  for (
                    let i = (variantsWithoutOld?.length || 0) - 1;
                    i >= 0;
                    i--
                  ) {
                    if (!variantsWithoutOld?.[i]?.why_tweak) {
                      lastNonModifiedIndex = i
                      break
                    }
                  }

                  if (lastNonModifiedIndex !== -1) {
                    // Insert right after the last non-modified variant
                    updatedVariants = [...(variantsWithoutOld || [])]
                    updatedVariants.splice(
                      lastNonModifiedIndex + 1,
                      0,
                      newVariant
                    )
                  } else {
                    // No non-modified variants exist, put at the beginning (before user-modified ones)
                    updatedVariants = [
                      newVariant,
                      ...(variantsWithoutOld || []),
                    ]
                  }
                } else if (
                  action === 'reject' &&
                  oldVariantIndex !== undefined &&
                  oldVariantIndex !== -1
                ) {
                  // Rejected non-modified variant maintains its position
                  updatedVariants = [...(variantsWithoutOld || [])]
                  updatedVariants.splice(oldVariantIndex, 0, newVariant)
                } else {
                  updatedVariants = [...(variantsWithoutOld || []), newVariant]
                }

                return {
                  ...element,
                  personalized_ids_to_approve: updatedIdsToApprove,
                  personalized_variants: updatedVariants,
                }
              }
              return element
            }),
          }
        }
      )
      onTweakSuccess?.(lastJsonMessage.personalizedVariant?.variant_id || '')
      return
    }

    // Backwards compatibility: Full content replacement (old backend response)
    if (lastJsonMessage.content) {
      queryClient.cancelQueries({
        queryKey: contentKeys.content(lastJsonMessage.content._id),
      })
      queryClient.setQueryData(
        contentKeys.content(lastJsonMessage.content._id),
        lastJsonMessage.content
      )
      queryClient.invalidateQueries({
        queryKey: contentKeys.content(lastJsonMessage.content._id),
      })
      onTweakSuccess?.(lastJsonMessage.personalizedVariant?.variant_id || '')
    }
  }

  useEffect(() => {
    if (content && selectedElement && lastJsonMessage) {
      const wsTopics = lastJsonMessage?.topics
      const rejectRegex = new RegExp(
        `content/${content._id}/element/([^/]+)/variant/([^/]+)/rejectVariant`
      )
      const rejectedTopic = wsTopics?.find((topic) => rejectRegex.test(topic))

      if (rejectedTopic) {
        const wsTopicElementId = rejectedTopic?.match(rejectRegex)?.[1]
        const wsTopicVariantId = rejectedTopic?.match(rejectRegex)?.[2]

        if (lastJsonMessage.status === 'success') {
          if (lastJsonMessage.sl) {
            dispatch({
              type: types.REJECT_VARIANT_SUCCESS,
              newVariant: lastJsonMessage.sl,
              oldVariantId: wsTopicVariantId,
            })
          }

          if (wsTopicElementId === String(selectedElement.element_id)) {
            const newVariantId =
              lastJsonMessage.sl?._id ||
              lastJsonMessage.baselineVariant?.sl_id ||
              lastJsonMessage.personalizedVariant?.variant_id
            if (newVariantId) {
              onTweakSuccess?.(newVariantId)
            }
          }

          if (
            selectedElement.personalization_id &&
            wsTopicElementId &&
            wsTopicVariantId
          ) {
            handlePersonalizedCampaign(
              wsTopicElementId,
              wsTopicVariantId,
              'reject'
            )
          } else if (lastJsonMessage.content) {
            // For non-personalized campaigns or backwards compatibility
            queryClient.cancelQueries({
              queryKey: contentKeys.content(lastJsonMessage.content._id),
            })
            queryClient.setQueryData(
              contentKeys.content(lastJsonMessage.content._id),
              lastJsonMessage.content
            )
            queryClient.invalidateQueries({
              queryKey: contentKeys.content(lastJsonMessage.content._id),
            })
          }
        } else if (lastJsonMessage.status === 'error') {
          errorToast('Something went wrong. Please try again.')
        }

        if (wsTopicElementId && wsTopicVariantId) {
          sendWebsocketMessage({
            action: 'rejectVariant',
            data: {
              contentId: content._id,
              elementId: Number(wsTopicElementId),
              variantId: wsTopicVariantId,
            },
            subscriptionAction: 'unsubscribe',
          })
        }
      }

      const tweakRegex = new RegExp(
        `content/${content._id}/element/([^/]+)/variant/([^/]+)/tweakVariant`
      )
      const tweakedTopic = wsTopics?.find((topic) => tweakRegex.test(topic))

      if (tweakedTopic) {
        const wsTopicElementId = tweakedTopic?.match(tweakRegex)?.[1]
        const wsTopicVariantId = tweakedTopic?.match(tweakRegex)?.[2]
        if (lastJsonMessage.status === 'success') {
          if (lastJsonMessage.sl) {
            dispatch({
              type: types.TWEAK_VARIANT_SUCCESS,
              newVariant: lastJsonMessage.sl,
              oldVariantId: lastJsonMessage.sl?.old_sl_id,
            })
          }

          if (wsTopicElementId === String(selectedElement.element_id)) {
            const newVariantId =
              lastJsonMessage.sl?._id ||
              lastJsonMessage.baselineVariant?.sl_id ||
              lastJsonMessage.personalizedVariant?.variant_id
            if (newVariantId) {
              onTweakSuccess?.(newVariantId)
            }
          }

          if (
            selectedElement.personalization_id &&
            wsTopicElementId &&
            wsTopicVariantId
          ) {
            handlePersonalizedCampaign(
              wsTopicElementId,
              wsTopicVariantId,
              'tweak'
            )
          } else if (lastJsonMessage.content) {
            // For non-personalized campaigns or backwards compatibility
            queryClient.cancelQueries({
              queryKey: contentKeys.content(lastJsonMessage.content._id),
            })
            queryClient.setQueryData(
              contentKeys.content(lastJsonMessage.content._id),
              lastJsonMessage.content
            )
            queryClient.invalidateQueries({
              queryKey: contentKeys.content(lastJsonMessage.content._id),
            })
          }
        } else if (lastJsonMessage.status === 'error') {
          errorToast('Something went wrong. Please try again.')
        }

        if (wsTopicElementId && wsTopicVariantId) {
          sendWebsocketMessage({
            action: 'tweakVariant',
            data: {
              contentId: content._id,
              elementId: Number(wsTopicElementId),
              variantId: wsTopicVariantId,
            },
            subscriptionAction: 'unsubscribe',
          })
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastJsonMessage])
}

export default useVariantTweakWebsocketSubscription
